import { createLazyModuleLoader } from './lazyModuleLoader.js';
import {
  buildReaderLifecycleInitContext,
  buildReaderKeyboardInitContext
} from './moduleInitContextBuilders.js';
import {
  ensureModuleWithInit,
  invokeLazyModuleMethod
} from './moduleRuntimeHelpers.js';

export function createReaderDomainRuntime(context = {}) {
  const {
    importWithChunkRecovery,
    state,
    elements,
    showToast,
    openModal,
    closeModal,
    addHighlight,
    updateHighlight,
    deleteHighlight,
    addCurrentBookmark,
    togglePanel,
    closeAllPanels,
    toggleDualPageMode,
    getTextSearch,
    getTts,
    getAutoScroll,
    getReadingTimer,
    syncModalOverlayTheme,
    syncThemeColor,
    loadSiteTheme,
    updateProgressDisplay,
    clearTocSearch,
    saveReadingProgress,
    updateBook,
    getBook,
    getReadingProgress,
    getReaderConstructor,
    waitForStableElementSize,
    loadToc,
    applyReaderSettings,
    maybeFixInitialRenderSize,
    showShortcutsHint,
    startReadingSession,
    initEnhancedFeatures,
    applyMobileReaderImmersive,
    renderBookmarks,
    restoreSavedMarks,
    showReadingEstimate,
    endReadingSession,
    cleanupEnhancedFeatures,
    hideSelectionToolbar,
    addBookmark,
    deleteBookmark,
    formatDate,
    toggleFullscreen,
    closeReader,
    navigatePage,
    handleHighlight,
    openQuickThought,
    toggleToc,
    getImmersiveMode,
    addThought,
    rerenderReaderForCurrentSize
  } = context;

  let readerEnhancementsApi = null;
  let readerEnhancementsLoadingPromise = null;
  let readerShareModuleApi = null;
  let readerShareModuleLoadingPromise = null;
  let readerAnnotationToolbarModuleApi = null;
  let readerAnnotationToolbarModuleLoadingPromise = null;
  let readerControlsPanelModuleApi = null;
  let readerControlsPanelModuleLoadingPromise = null;
  let readerSettingsModuleApi = null;
  let readerSettingsModuleLoadingPromise = null;
  let readerTocSearchModuleApi = null;
  let readerTocSearchModuleLoadingPromise = null;
  let readerTocModuleApi = null;
  let readerTocModuleLoadingPromise = null;
  let readerProgressModuleApi = null;
  let readerProgressModuleLoadingPromise = null;
  let readerLifecycleModuleApi = null;
  let readerLifecycleModuleLoadingPromise = null;
  let readerBookmarksModuleApi = null;
  let readerBookmarksModuleLoadingPromise = null;
  let readerKeyboardModuleApi = null;
  let readerKeyboardModuleLoadingPromise = null;
  let readerDisplayModeModuleApi = null;
  let readerDisplayModeModuleLoadingPromise = null;
  let readerUiPanelsModuleApi = null;
  let readerUiPanelsModuleLoadingPromise = null;
  let readerRenderSizingModuleApi = null;
  let readerRenderSizingModuleLoadingPromise = null;
  let readerStatsModuleApi = null;
  let readerStatsModuleLoadingPromise = null;
  let readerQuickThoughtModuleApi = null;
  let readerQuickThoughtModuleLoadingPromise = null;
  let readerImmersiveModuleApi = null;
  let readerImmersiveModuleLoadingPromise = null;

  const loadReaderEnhancementsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerEnhancements.js'),
    getApi: () => readerEnhancementsApi,
    setApi: (mod) => {
      readerEnhancementsApi = mod;
    },
    getLoadingPromise: () => readerEnhancementsLoadingPromise,
    setLoadingPromise: (promise) => {
      readerEnhancementsLoadingPromise = promise;
    }
  });

  const loadReaderShareModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerShareModule.js'),
    getApi: () => readerShareModuleApi,
    setApi: (mod) => {
      readerShareModuleApi = mod;
    },
    getLoadingPromise: () => readerShareModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerShareModuleLoadingPromise = promise;
    }
  });

  function ensureReaderShareModuleReady() {
    return ensureModuleWithInit(loadReaderShareModule, {
      isInitializedExport: 'isReaderShareModuleInitialized',
      initExport: 'initReaderShareModule',
      createInitContext: () => ({
        state,
        elements,
        openModal,
        closeModal,
        showToast,
        loadReaderEnhancementsModule
      })
    });
  }

  const loadReaderAnnotationToolbarModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerAnnotationToolbarModule.js'),
    getApi: () => readerAnnotationToolbarModuleApi,
    setApi: (mod) => {
      readerAnnotationToolbarModuleApi = mod;
    },
    getLoadingPromise: () => readerAnnotationToolbarModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerAnnotationToolbarModuleLoadingPromise = promise;
    }
  });

  function ensureReaderAnnotationToolbarModuleReady() {
    return ensureModuleWithInit(loadReaderAnnotationToolbarModule, {
      isInitializedExport: 'isReaderAnnotationToolbarModuleInitialized',
      initExport: 'initReaderAnnotationToolbarModule',
      createInitContext: () => ({
        state,
        elements,
        showToast,
        openModal,
        closeModal,
        addHighlight,
        updateHighlight,
        deleteHighlight,
        addCurrentBookmark
      })
    });
  }

  function invokeReaderAnnotationToolbar(fnName, ...args) {
    invokeLazyModuleMethod(
      ensureReaderAnnotationToolbarModuleReady,
      fnName,
      args,
      () => {
        showToast('标注工具加载失败');
      }
    );
  }

  const loadReaderControlsPanelModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerControlsPanelModule.js'),
    getApi: () => readerControlsPanelModuleApi,
    setApi: (mod) => {
      readerControlsPanelModuleApi = mod;
    },
    getLoadingPromise: () => readerControlsPanelModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerControlsPanelModuleLoadingPromise = promise;
    }
  });

  function ensureReaderControlsPanelModuleReady() {
    return ensureModuleWithInit(loadReaderControlsPanelModule, {
      isInitializedExport: 'isReaderControlsPanelModuleInitialized',
      initExport: 'initReaderControlsPanelModule',
      createInitContext: () => ({
        state,
        elements,
        togglePanel,
        closeAllPanels,
        addCurrentBookmark,
        toggleDualPageMode,
        getTextSearch,
        getTts,
        getAutoScroll,
        getReadingTimer
      })
    });
  }

  function invokeReaderControlsPanel(fnName, ...args) {
    invokeLazyModuleMethod(
      ensureReaderControlsPanelModuleReady,
      fnName,
      args,
      () => {
        showToast('阅读器控制模块加载失败');
      }
    );
  }

  const loadReaderSettingsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerSettingsModule.js'),
    getApi: () => readerSettingsModuleApi,
    setApi: (mod) => {
      readerSettingsModuleApi = mod;
    },
    getLoadingPromise: () => readerSettingsModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerSettingsModuleLoadingPromise = promise;
    }
  });

  function ensureReaderSettingsModuleReady() {
    return ensureModuleWithInit(loadReaderSettingsModule, {
      isInitializedExport: 'isReaderSettingsModuleInitialized',
      initExport: 'initReaderSettingsModule',
      createInitContext: () => ({
        state,
        elements,
        syncModalOverlayTheme,
        syncThemeColor,
        loadSiteTheme
      })
    });
  }

  function invokeReaderSettings(fnName, ...args) {
    invokeLazyModuleMethod(
      ensureReaderSettingsModuleReady,
      fnName,
      args,
      () => {
        showToast('阅读设置模块加载失败');
      }
    );
  }

  const loadReaderTocSearchModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerTocSearchModule.js'),
    getApi: () => readerTocSearchModuleApi,
    setApi: (mod) => {
      readerTocSearchModuleApi = mod;
    },
    getLoadingPromise: () => readerTocSearchModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerTocSearchModuleLoadingPromise = promise;
    }
  });

  function ensureReaderTocSearchModuleReady() {
    return ensureModuleWithInit(loadReaderTocSearchModule, {
      isInitializedExport: 'isReaderTocSearchModuleInitialized',
      initExport: 'initReaderTocSearchModule',
      createInitContext: () => ({
        state,
        elements,
        updateProgressDisplay
      })
    });
  }

  const loadReaderTocModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerTocModule.js'),
    getApi: () => readerTocModuleApi,
    setApi: (mod) => {
      readerTocModuleApi = mod;
    },
    getLoadingPromise: () => readerTocModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerTocModuleLoadingPromise = promise;
    }
  });

  function ensureReaderTocModuleReady() {
    return ensureModuleWithInit(loadReaderTocModule, {
      isInitializedExport: 'isReaderTocModuleInitialized',
      initExport: 'initReaderTocModule',
      createInitContext: () => ({
        state,
        elements,
        updateProgressDisplay,
        closeAllPanels,
        clearTocSearch
      })
    });
  }

  const loadReaderProgressModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerProgressModule.js'),
    getApi: () => readerProgressModuleApi,
    setApi: (mod) => {
      readerProgressModuleApi = mod;
    },
    getLoadingPromise: () => readerProgressModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerProgressModuleLoadingPromise = promise;
    }
  });

  function ensureReaderProgressModuleReady() {
    return ensureModuleWithInit(loadReaderProgressModule, {
      isInitializedExport: 'isReaderProgressModuleInitialized',
      initExport: 'initReaderProgressModule',
      createInitContext: () => ({
        state,
        elements,
        saveReadingProgress,
        updateBook,
        onAfterMobiPage: () => {
          invokeReaderAnnotationToolbar('restoreSavedMarks');
          invokeReaderAnnotationToolbar('bindIframeSelectionEvents');
        }
      })
    });
  }

  const loadReaderLifecycleModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerLifecycleModule.js'),
    getApi: () => readerLifecycleModuleApi,
    setApi: (mod) => {
      readerLifecycleModuleApi = mod;
    },
    getLoadingPromise: () => readerLifecycleModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerLifecycleModuleLoadingPromise = promise;
    }
  });

  function ensureReaderLifecycleModuleReady() {
    return ensureModuleWithInit(loadReaderLifecycleModule, {
      isInitializedExport: 'isReaderLifecycleModuleInitialized',
      initExport: 'initReaderLifecycleModule',
      createInitContext: () => buildReaderLifecycleInitContext({
        state,
        elements,
        showToast,
        getBook,
        getReadingProgress,
        getReaderConstructor,
        waitForStableElementSize,
        loadToc,
        updateProgressDisplay,
        applyReaderSettings,
        maybeFixInitialRenderSize,
        showShortcutsHint,
        startReadingSession,
        updateBook,
        initEnhancedFeatures,
        applyMobileReaderImmersive,
        renderBookmarks,
        restoreSavedMarks,
        showReadingEstimate,
        endReadingSession,
        cleanupEnhancedFeatures,
        closeAllPanels,
        hideSelectionToolbar,
        syncModalOverlayTheme,
        syncThemeColor,
        saveReadingProgress
      })
    });
  }

  const loadReaderBookmarksModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerBookmarksModule.js'),
    getApi: () => readerBookmarksModuleApi,
    setApi: (mod) => {
      readerBookmarksModuleApi = mod;
    },
    getLoadingPromise: () => readerBookmarksModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerBookmarksModuleLoadingPromise = promise;
    }
  });

  function ensureReaderBookmarksModuleReady() {
    return ensureModuleWithInit(loadReaderBookmarksModule, {
      isInitializedExport: 'isReaderBookmarksModuleInitialized',
      initExport: 'initReaderBookmarksModule',
      createInitContext: () => ({
        state,
        elements,
        showToast,
        addBookmark,
        deleteBookmark,
        formatDate,
        togglePanel,
        updateProgressDisplay,
        closeAllPanels
      })
    });
  }

  const loadReaderKeyboardModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerKeyboardModule.js'),
    getApi: () => readerKeyboardModuleApi,
    setApi: (mod) => {
      readerKeyboardModuleApi = mod;
    },
    getLoadingPromise: () => readerKeyboardModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerKeyboardModuleLoadingPromise = promise;
    }
  });

  function ensureReaderKeyboardModuleReady() {
    return ensureModuleWithInit(loadReaderKeyboardModule, {
      isInitializedExport: 'isReaderKeyboardModuleInitialized',
      initExport: 'initReaderKeyboardModule',
      createInitContext: () => buildReaderKeyboardInitContext({
        state,
        elements,
        closeAllPanels,
        toggleFullscreen,
        closeReader,
        navigatePage,
        handleHighlight,
        openQuickThought,
        toggleToc,
        addCurrentBookmark,
        showToast,
        toggleDualPageMode,
        togglePanel,
        showShortcutsHint,
        closeModal,
        getTts,
        getAutoScroll,
        getImmersiveMode
      })
    });
  }

  const loadReaderDisplayModeModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerDisplayModeModule.js'),
    getApi: () => readerDisplayModeModuleApi,
    setApi: (mod) => {
      readerDisplayModeModuleApi = mod;
    },
    getLoadingPromise: () => readerDisplayModeModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerDisplayModeModuleLoadingPromise = promise;
    }
  });

  function ensureReaderDisplayModeModuleReady() {
    return ensureModuleWithInit(loadReaderDisplayModeModule, {
      isInitializedExport: 'isReaderDisplayModeModuleInitialized',
      initExport: 'initReaderDisplayModeModule',
      createInitContext: () => ({
        state,
        elements,
        showToast,
        updateProgressDisplay
      })
    });
  }

  const loadReaderUiPanelsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerUiPanelsModule.js'),
    getApi: () => readerUiPanelsModuleApi,
    setApi: (mod) => {
      readerUiPanelsModuleApi = mod;
    },
    getLoadingPromise: () => readerUiPanelsModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerUiPanelsModuleLoadingPromise = promise;
    }
  });

  function ensureReaderUiPanelsModuleReady() {
    return ensureModuleWithInit(loadReaderUiPanelsModule, {
      isInitializedExport: 'isReaderUiPanelsModuleInitialized',
      initExport: 'initReaderUiPanelsModule',
      createInitContext: () => ({
        elements
      })
    });
  }

  const loadReaderRenderSizingModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerRenderSizingModule.js'),
    getApi: () => readerRenderSizingModuleApi,
    setApi: (mod) => {
      readerRenderSizingModuleApi = mod;
    },
    getLoadingPromise: () => readerRenderSizingModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerRenderSizingModuleLoadingPromise = promise;
    }
  });

  function ensureReaderRenderSizingModuleReady() {
    return ensureModuleWithInit(loadReaderRenderSizingModule, {
      isInitializedExport: 'isReaderRenderSizingModuleInitialized',
      initExport: 'initReaderRenderSizingModule',
      createInitContext: () => ({
        state,
        elements
      })
    });
  }

  const loadReaderStatsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerStatsModule.js'),
    getApi: () => readerStatsModuleApi,
    setApi: (mod) => {
      readerStatsModuleApi = mod;
    },
    getLoadingPromise: () => readerStatsModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerStatsModuleLoadingPromise = promise;
    }
  });

  function ensureReaderStatsModuleReady() {
    return ensureModuleWithInit(loadReaderStatsModule, {
      isInitializedExport: 'isReaderStatsModuleInitialized',
      initExport: 'initReaderStatsModule',
      createInitContext: () => ({
        state
      })
    });
  }

  const loadReaderQuickThoughtModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerQuickThoughtModule.js'),
    getApi: () => readerQuickThoughtModuleApi,
    setApi: (mod) => {
      readerQuickThoughtModuleApi = mod;
    },
    getLoadingPromise: () => readerQuickThoughtModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerQuickThoughtModuleLoadingPromise = promise;
    }
  });

  function ensureReaderQuickThoughtModuleReady() {
    return ensureModuleWithInit(loadReaderQuickThoughtModule, {
      isInitializedExport: 'isReaderQuickThoughtModuleInitialized',
      initExport: 'initReaderQuickThoughtModule',
      createInitContext: () => ({
        state,
        elements,
        addThought,
        showToast,
        openModal,
        closeModal
      })
    });
  }

  const loadReaderImmersiveModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./readerImmersiveModule.js'),
    getApi: () => readerImmersiveModuleApi,
    setApi: (mod) => {
      readerImmersiveModuleApi = mod;
    },
    getLoadingPromise: () => readerImmersiveModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      readerImmersiveModuleLoadingPromise = promise;
    }
  });

  function ensureReaderImmersiveModuleReady() {
    return ensureModuleWithInit(loadReaderImmersiveModule, {
      isInitializedExport: 'isReaderImmersiveModuleInitialized',
      initExport: 'initReaderImmersiveModule',
      createInitContext: () => ({
        elements,
        getImmersiveMode,
        rerenderReaderForCurrentSize
      })
    });
  }

  return {
    loadReaderEnhancementsModule,
    ensureReaderShareModuleReady,
    getReaderShareModuleApi: () => readerShareModuleApi,
    ensureReaderAnnotationToolbarModuleReady,
    invokeReaderAnnotationToolbar,
    getReaderAnnotationToolbarModuleApi: () => readerAnnotationToolbarModuleApi,
    ensureReaderControlsPanelModuleReady,
    invokeReaderControlsPanel,
    getReaderControlsPanelModuleApi: () => readerControlsPanelModuleApi,
    ensureReaderSettingsModuleReady,
    invokeReaderSettings,
    getReaderSettingsModuleApi: () => readerSettingsModuleApi,
    ensureReaderTocSearchModuleReady,
    getReaderTocSearchModuleApi: () => readerTocSearchModuleApi,
    ensureReaderTocModuleReady,
    getReaderTocModuleApi: () => readerTocModuleApi,
    ensureReaderProgressModuleReady,
    getReaderProgressModuleApi: () => readerProgressModuleApi,
    ensureReaderLifecycleModuleReady,
    getReaderLifecycleModuleApi: () => readerLifecycleModuleApi,
    ensureReaderBookmarksModuleReady,
    getReaderBookmarksModuleApi: () => readerBookmarksModuleApi,
    ensureReaderKeyboardModuleReady,
    getReaderKeyboardModuleApi: () => readerKeyboardModuleApi,
    ensureReaderDisplayModeModuleReady,
    getReaderDisplayModeModuleApi: () => readerDisplayModeModuleApi,
    ensureReaderUiPanelsModuleReady,
    getReaderUiPanelsModuleApi: () => readerUiPanelsModuleApi,
    ensureReaderRenderSizingModuleReady,
    getReaderRenderSizingModuleApi: () => readerRenderSizingModuleApi,
    ensureReaderStatsModuleReady,
    getReaderStatsModuleApi: () => readerStatsModuleApi,
    ensureReaderQuickThoughtModuleReady,
    getReaderQuickThoughtModuleApi: () => readerQuickThoughtModuleApi,
    ensureReaderImmersiveModuleReady,
    getReaderImmersiveModuleApi: () => readerImmersiveModuleApi
  };
}
